import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import {
  FileTextIcon,
  DownloadIcon,
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  AlertCircleIcon,
  EyeIcon,
  CalendarIcon,
} from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

interface Invoice {
  id: number;
  invoicenum: string;
  total: string;
  balance: string;
  status: string; // WHMCS status: 'Paid', 'Unpaid', 'Cancelled', etc.
  duedate: string;
  date: string;
  paymentmethod: string;
  userid: number;
  subtotal: string;
  tax: string;
  notes: string;
}

const InvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  const [activeFilter, setActiveFilter] = useState<string>('unpaid'); // Iniciar com filtro de pendentes
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const invoicesPerPage = 10;

  useEffect(() => {
    fetchInvoices();
  }, []);

  // Listen for payment confirmation events to refresh the invoice list
  useEffect(() => {
    const handlePaymentConfirmed = () => {
      fetchInvoices();
    };

    window.addEventListener('payment_confirmed', handlePaymentConfirmed as EventListener);

    return () => {
      window.removeEventListener('payment_confirmed', handlePaymentConfirmed as EventListener);
    };
  }, []);

  const fetchInvoices = async (page = 1) => {
    try {
      setIsLoading(true);
      setError('');

      const params = {
        page,
        limit: invoicesPerPage,
      };

      const response = await api.get('/invoices', { params });

      // Extract invoices and pagination from nested API response structure
      const invoicesData = response.data?.data?.invoices || [];
      const paginationData = response.data?.data?.pagination || {};

      // Ordenar faturas por data de vencimento (crescente)
      const sortedInvoices = [...invoicesData].sort((a, b) => {
        return new Date(a.duedate).getTime() - new Date(b.duedate).getTime();
      });

      setInvoices(sortedInvoices);
      setTotalInvoices(paginationData.total || 0);
      setTotalPages(paginationData.pages || 0);
      setCurrentPage(page);

      // Inicialmente, aplicar o filtro de pendentes (será feito pelo useEffect)
    } catch (err: any) {
      setError('Erro ao carregar faturas');
      console.error('💥 [FRONTEND] Erro ao buscar faturas:', err);
      console.error('💥 [FRONTEND] Detalhes do erro:', err.response?.data);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayInvoice = (invoiceId: number) => {
    // Navigate to the invoice page where users can see PIX QR code and payment details
    navigate(`/invoice/${invoiceId}`);
  };

  const handleDownloadInvoice = async (invoiceId: number) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/download`, {
        responseType: 'blob',
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fatura-${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError('Erro ao baixar fatura');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'unpaid':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'overdue':
        return <AlertCircleIcon className="h-5 w-5 text-red-500" />;
      case 'cancelled':
        return <XCircleIcon className="h-5 w-5 text-gray-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'Pago';
      case 'unpaid':
        return 'Pendente';
      case 'overdue':
        return 'Vencido';
      case 'cancelled':
        return 'Cancelado';
      case 'refunded':
        return 'Reembolsado';
      case 'collections':
        return 'Em Cobrança';
      case 'draft':
        return 'Rascunho';
      case 'payment_pending':
        return 'Pagamento Pendente';
      case 'fraud':
        return 'Fraude';
      case 'void':
        return 'Anulada';
      default:
        return status; // Mostrar status original se não reconhecido
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'unpaid':
        return 'bg-yellow-100 text-yellow-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount: string | number) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(numAmount || 0);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('pt-BR');
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('pt-BR') + ' ' +
           date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const isOverdue = (dueDate: string, status: string) => {
    return status.toLowerCase() === 'unpaid' && new Date(dueDate) < new Date();
  };

  const openInvoiceModal = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      fetchInvoices(page);
    }
  };

  // Função para aplicar filtro às faturas
  const applyFilter = (filter: string) => {
    setActiveFilter(filter);
    
    let filtered: Invoice[] = [];
    
    switch (filter) {
      case 'all':
        filtered = invoices;
        break;
      case 'paid':
        filtered = invoices.filter(inv => inv.status.toLowerCase() === 'paid');
        break;
      case 'unpaid':
        filtered = invoices.filter(inv => inv.status.toLowerCase() === 'unpaid');
        break;
      case 'overdue':
        filtered = invoices.filter(inv => isOverdue(inv.duedate, inv.status));
        break;
      default:
        filtered = invoices;
    }
    
    // Manter a ordenação por data de vencimento
    filtered = [...filtered].sort((a, b) => {
      return new Date(a.duedate).getTime() - new Date(b.duedate).getTime();
    });
    
    setFilteredInvoices(filtered);
  };

  // Aplicar filtro inicial quando as faturas são carregadas
  useEffect(() => {
    applyFilter(activeFilter);
  }, [invoices]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Faturas</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerencie suas faturas e pagamentos
          </p>
        </div>
        {/* Removido contador de faturas do cabeçalho */}
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Filtros */}
      <div className="mb-6 flex flex-wrap gap-2">
        <button
          onClick={() => applyFilter('all')}
          className={`px-4 py-2 rounded-md text-sm font-medium ${
            activeFilter === 'all'
              ? 'bg-cyan-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Todas
        </button>
        <button
          onClick={() => applyFilter('paid')}
          className={`px-4 py-2 rounded-md text-sm font-medium ${
            activeFilter === 'paid'
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Pagas
        </button>
        <button
          onClick={() => applyFilter('unpaid')}
          className={`px-4 py-2 rounded-md text-sm font-medium ${
            activeFilter === 'unpaid'
              ? 'bg-yellow-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Pendentes
        </button>
        <button
          onClick={() => applyFilter('overdue')}
          className={`px-4 py-2 rounded-md text-sm font-medium ${
            activeFilter === 'overdue'
              ? 'bg-red-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Vencidas
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer" onClick={() => applyFilter('paid')}>
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Pagas
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {invoices.filter((inv) => inv.status.toLowerCase() === 'paid').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer" onClick={() => applyFilter('unpaid')}>
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Pendentes
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {invoices.filter((inv) => inv.status.toLowerCase() === 'unpaid').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer" onClick={() => applyFilter('overdue')}>
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertCircleIcon className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Vencidas
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {
                      invoices.filter((inv) =>
                        isOverdue(inv.duedate, inv.status)
                      ).length
                    }
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer" onClick={() => applyFilter('all')}>
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CreditCardIcon className="h-6 w-6 text-cyan-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Pago
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(
                      invoices
                        .filter((inv) => inv.status.toLowerCase() === 'paid')
                        .reduce((sum, inv) => sum + parseFloat(inv.total || '0'), 0)
                    )}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoices List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {filteredInvoices.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <FileTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              Nenhuma fatura encontrada
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Suas faturas aparecerão aqui quando forem geradas.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {filteredInvoices.map((invoice) => (
              <li key={invoice.id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <FileTextIcon className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-gray-900">
                            Fatura #{invoice.id}
                          </p>
                          <div className="ml-2 flex items-center">
                            {getStatusIcon(invoice.status)}
                          </div>
                        </div>
                        <div className="mt-1 flex items-center space-x-4">
                          <p className="text-sm text-gray-500">
                            {invoice.notes || `Fatura ${invoice.invoicenum || ''}`}
                          </p>
                          <div className="flex items-center text-sm text-gray-500">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            {invoice.status.toLowerCase() === 'paid'
                              ? `Pago em: ${formatDateTime(invoice.date)}`
                              : `Vencimento: ${formatDate(invoice.duedate)}`}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {formatCurrency(invoice.total)}
                        </p>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            invoice.status
                          )}`}
                        >
                          {getStatusText(invoice.status)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openInvoiceModal(invoice)}
                          className="text-gray-400 hover:text-gray-600"
                          title="Ver detalhes"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDownloadInvoice(invoice.id)}
                          className="text-gray-400 hover:text-gray-600"
                          title="Baixar PDF"
                        >
                          <DownloadIcon className="h-4 w-4" />
                        </button>
                        {invoice.status.toLowerCase() === 'unpaid' && (
                          <button
                            onClick={() => handlePayInvoice(invoice.id)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                          >
                            <CreditCardIcon className="h-3 w-3 mr-1" />
                            Pagar
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  {isOverdue(invoice.duedate, invoice.status) && (
                    <div className="mt-2 px-4 py-2 bg-red-50 rounded-md">
                      <p className="text-sm text-red-700">
                        ⚠️ Esta fatura está vencida há{' '}
                        {Math.floor(
                          (new Date().getTime() - new Date(invoice.duedate).getTime()) /
                            (1000 * 60 * 60 * 24)
                        )}{' '}
                        dias
                      </p>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próximo
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Mostrando <span className="font-medium">{(currentPage - 1) * invoicesPerPage + 1}</span> até{' '}
                  <span className="font-medium">{Math.min(currentPage * invoicesPerPage, totalInvoices)}</span> de{' '}
                  <span className="font-medium">{totalInvoices}</span> resultados
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Anterior</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === currentPage
                            ? 'z-10 bg-cyan-50 border-cyan-500 text-cyan-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Próximo</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invoice Details Modal */}
      {showInvoiceModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl border border-gray-200">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">
                    Detalhes da Fatura
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Informações completas da fatura #{selectedInvoice.invoicenum}
                  </p>
                </div>
                <button
                  onClick={() => setShowInvoiceModal(false)}
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-5">
                <div className="bg-gray-50 rounded-xl p-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Número da Fatura
                  </label>
                  <p className="text-lg font-medium text-gray-900">
                    #{selectedInvoice.id}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Descrição
                  </label>
                  <p className="text-gray-900">
                    {selectedInvoice.notes || `Fatura ${selectedInvoice.invoicenum || ''}`}
                  </p>
                </div>
                <div className="bg-cyan-50 rounded-xl p-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Valor
                  </label>
                  <p className="text-2xl font-bold text-cyan-600">
                    {formatCurrency(selectedInvoice.total)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Status
                  </label>
                  <div className="flex items-center">
                    {getStatusIcon(selectedInvoice.status)}
                    <span className="ml-2 text-lg font-medium text-gray-900">
                      {getStatusText(selectedInvoice.status)}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Vencimento
                    </label>
                    <p className="text-gray-900">
                      {formatDate(selectedInvoice.duedate)}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      {selectedInvoice.status.toLowerCase() === 'paid' ? 'Data de Pagamento' : 'Criação'}
                    </label>
                    <p className="text-gray-900">
                      {selectedInvoice.status.toLowerCase() === 'paid'
                        ? formatDateTime(selectedInvoice.date)
                        : formatDate(selectedInvoice.date)}
                    </p>
                  </div>
                </div>
                {selectedInvoice.paymentmethod && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Método de Pagamento
                    </label>
                    <p className="text-gray-900">
                      {selectedInvoice.paymentmethod}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 mt-6">
                <button
                  onClick={() => handleDownloadInvoice(selectedInvoice.id)}
                  className="inline-flex items-center px-6 py-3 border-2 border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all"
                >
                  <DownloadIcon className="h-5 w-5 mr-2" />
                  Baixar PDF
                </button>
                {selectedInvoice.status.toLowerCase() === 'unpaid' && (
                  <button
                    onClick={() => {
                      handlePayInvoice(selectedInvoice.id);
                      setShowInvoiceModal(false);
                    }}
                    className="inline-flex items-center px-6 py-3 border border-transparent rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all transform hover:scale-105"
                  >
                    <CreditCardIcon className="h-5 w-5 mr-2" />
                    Pagar Agora
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;